import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import * as api from '../../api'
import { ApiError } from '../../api'
import { scrollIntoFrame } from '../../animations/scrollIntoFrame'
import type { DietaryRequirement, Invite, InvitedPerson } from '../../types'
import { DietaryPicker } from './DietaryPicker'
import { GuestDietarySummary } from './GuestDietarySummary'

interface GuestDietaryFormProps {
  person: InvitedPerson
  onSaved: (invite: Invite) => void
  /** Lets the section word its introduction for what is actually on screen. */
  onEditingChange: (personId: string, editing: boolean) => void
  onSessionLost: () => void
}

/**
 * One guest's dietary requirements, submitted on their own.
 *
 * Deliberately per person rather than per invite: a household replies to the
 * invitation once, but the kitchen needs an answer for each of them, and nobody
 * should have to collect everyone's before saving any. This is also why it is a
 * separate form from the RSVP entirely - the two are answered at different
 * times, often weeks apart.
 *
 * Day guests and evening guests are asked the same question: there is food at
 * both ends of the day.
 */
export function GuestDietaryForm({
  person,
  onSaved,
  onEditingChange,
  onSessionLost,
}: GuestDietaryFormProps) {
  const [dietary, setDietary] = useState<DietaryRequirement[]>(() => [...person.dietary])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Whether the summary is arriving because they just finished, which is the
  // only time there is anything worth revealing.
  const [justFinished, setJustFinished] = useState(false)

  const namePrefix = `dietary-${person.person_id}`

  // A guest we have everything from lands on the confirmation rather than the
  // form again - the same way a guest who has already replied lands on the RSVP
  // confirmation. Whether we have everything is the server's call.
  const [editing, setEditing] = useState(!person.dietary_complete)

  useEffect(() => {
    onEditingChange(person.person_id, editing)
  }, [editing, person.person_id, onEditingChange])

  /*
   * Bring the form's top into frame when it opens, the mirror of what the
   * summary does when it replaces it. The swap is a modest one, but a guest who
   * asks to change something should still land on the thing they asked for
   * rather than somewhere beside it.
   *
   * A ref rather than state for the flag, because it only needs to survive to
   * the layout effect and should not cause a render of its own. It is set only
   * by the guest asking to change something, so a page that simply arrives with
   * an unanswered form does not scroll itself.
   */
  const formRef = useRef<HTMLFormElement>(null)
  const openedByGuest = useRef(false)

  useLayoutEffect(() => {
    if (!editing || !openedByGuest.current || !formRef.current) return
    openedByGuest.current = false
    scrollIntoFrame(formRef.current, 'start')
  }, [editing])

  function toggleDietary(requirement: DietaryRequirement) {
    setDietary((current) =>
      current.includes(requirement)
        ? current.filter((r) => r !== requirement)
        : [...current, requirement],
    )
    setError(null)
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (saving) return

    setSaving(true)
    setError(null)
    try {
      const invite = await api.setDietary(person.person_id, { dietary })
      onSaved(invite)
      setJustFinished(true)
      setEditing(false)
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthenticated) {
        onSessionLost()
        return
      }
      setError(err instanceof ApiError ? err.message : 'We could not save that. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  function handleChangeAgain() {
    // Reset from the invite rather than keeping the local draft, so the form
    // opens on what is actually saved - it may have been changed elsewhere.
    setDietary([...person.dietary])
    openedByGuest.current = true
    setEditing(true)
    setJustFinished(false)
    setError(null)
  }

  if (!editing) {
    return (
      <GuestDietarySummary person={person} reveal={justFinished} onChange={handleChangeAgain} />
    )
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="border-ink/10 bg-parchment/70 rounded-lg border p-5 shadow-sm sm:p-6"
    >
      <h3 className="font-display text-xl">{person.name}</h3>

      <div className="mt-4">
        <DietaryPicker
          selected={dietary}
          disabled={saving}
          namePrefix={namePrefix}
          onToggle={toggleDietary}
        />
      </div>

      {error && (
        <p role="alert" className="text-claret mt-4 text-sm">
          {error}
        </p>
      )}

      <div className="mt-5">
        <button
          type="submit"
          disabled={saving}
          className="bg-claret hover:bg-claret-dark rounded-md px-5 py-2.5 text-sm tracking-widest text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving
            ? 'Saving…'
            : // "Confirm" rather than "Send", because it has to be pressed even
              // with nothing ticked and "send" implies there is something to.
              `Confirm ${person.name.split(' ')[0]}'s requirements`}
        </button>
      </div>
    </form>
  )
}
